import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { displaySuccess } from 'Helpers/FacetNotificationManager';
import { ACTIVE, CHURNED } from 'Classes/Household';
import { getDependentOptions } from 'Helpers/Meta';
import fr from 'facet-rest';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import {
  FDAutoCompleteInput, FDBox, FDStack, FDTitleText, FDBodyText,
} from '@facet-wealth/design';
import { ClientFlyoutSignatureDates as SignatureDates } from '../../../../../../UI/Signature';

const ServiceSection = ({ household, householdResource, updateHousehold }) => {
  const meta = householdResource.meta().export();
  const isActiveHousehold = household.status === ACTIVE;
  const isChurned = household.status === CHURNED;
  const [csmId, setCsmId] = useState(household.csManagerId);
  const [plannerId, setPlannerId] = useState(household.leadPlannerId);
  const [loading, setLoading] = useState(false);
  const clients = (household.clients || []).map(client => fr.resource(client));

  useEffect(() => {
    if (csmId !== householdResource.attr('csManagerId')) {
      setLoading(true);
      const req = householdResource
        .copy()
        .attr('csManagerId', csmId?.toString());
      updateHousehold(req.export()).then(() => {
        displaySuccess('Updated CSM');
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [csmId]);

  useEffect(() => {
    if (plannerId !== householdResource.attr('leadPlannerId')) {
      setLoading(true);
      const req = householdResource
        .copy()
        .attr('leadPlannerId', plannerId?.toString());
      updateHousehold(req.export()).then(() => {
        displaySuccess('Updated Lead Planner');
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [plannerId]);

  let title = 'Prospective Client';

  if (isChurned) {
    title = 'Churned Client';
  }

  if (isActiveHousehold) {
    title = 'Active Client';
  }

  const plannerOptions = getDependentOptions(meta.template, 'leadPlannerId', household.isTestHousehold);
  const plannerValue = plannerOptions.find(element => element.id === `${plannerId}`);

  const csmOptions = getDependentOptions(meta.template, 'csManagerId', household.isTestHousehold);
  const csmValue = csmOptions.find(element => element.id === `${csmId}`);

  return (
    <FDBox>
      <FDStack spacing={2} direction={'column'}>
        <FDStack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <FDTitleText value={'CSM'} />
          <FDAutoCompleteInput
            options={csmOptions}
            label={''}
            disabled={loading}
            onChange={(id) => {
              // Don't attempt to set CsmId on null value
              if (!id) {
                return;
              }

              setCsmId(id);
            }}
            value={csmValue?.id ? `${csmValue.id}` : ''}
            size={'sm'}
          />
        </FDStack>
        <FDStack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <FDTitleText value={'Planner'} />
          <FDAutoCompleteInput
            options={plannerOptions}
            label={''}
            disabled={loading}
            onChange={(id) => {
              // Don't attempt to set LeadPlannerId on null value
              if (!id) {
                return;
              }

              // If the household is active, get confirmation they want to change the lead planner
              // eslint-disable-next-line no-restricted-globals, no-alert
              const confirmed = confirm(
                'Are you sure you want to change Lead Planners?',
              );
              if (confirmed) {
                setPlannerId(id);
              }
            }}
            value={plannerValue?.id ? `${plannerValue.id}` : ''}
            size={'sm'}
          />
        </FDStack>
        {!isActiveHousehold && (
        <FDBox>
          <FDTitleText value={title} />
          <FDBodyText value={'Make a mistake? Want to update services provided? Change billing method?'} size={'sm'} />
        </FDBox>
        )}
      </FDStack>

      {isActiveHousehold && (
      <FDBox>
        <FDTitleText value={'Planning Agreement'} />
        <SignatureDates clients={clients} />
      </FDBox>
      )}
      {loading && (
        <Typography variant={'h4'} color={'primaryGray'} className="section-row">
          {'Saving...'}
        </Typography>
      )}
    </FDBox>
  );
};

ServiceSection.propTypes = {
  household: PropTypes.object.isRequired,
  householdResource: PropTypes.object.isRequired,
  updateHousehold: PropTypes.func.isRequired,
};

export default ServiceSection;
